import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Loader2, Lock, Globe, Search, X, UserPlus, Users, Church } from "lucide-react";
import { createGroup, searchUsers, inviteToGroup, getMyParishesForInvite } from "@/lib/api";
import { categoryIcons, categoryColors } from "../constants";

export default function CreateGroupModal({ isOpen, onClose, categories, onSuccess, currentUser }) {
  const [step, setStep] = useState(1); // 1 = group details, 2 = invite members
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("General");
  const [privacy, setPrivacy] = useState("public");
  const [loading, setLoading] = useState(false);
  const [createdGroupId, setCreatedGroupId] = useState(null);
  
  // Member invitation state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  
  // Parish-only state
  const [userParishes, setUserParishes] = useState([]);
  const [selectedParishIds, setSelectedParishIds] = useState([]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setName("");
      setDescription("");
      setCategory("General");
      setPrivacy("public");
      setCreatedGroupId(null);
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUsers([]);
      setInviteMessage("");
      setSelectedParishIds([]);
    }
  }, [isOpen]);
  
  // Fetch user's parishes when modal opens
  useEffect(() => {
    if (isOpen) {
      getMyParishesForInvite().then(res => {
        setUserParishes(res.data.parishes || []);
      }).catch(() => setUserParishes([]));
    }
  }, [isOpen]);

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await searchUsers({ search: searchQuery, limit: 20 });
      // Filter out current user and already selected users
      const filtered = (res.data.users || []).filter(
        u => u.user_id !== currentUser?.user_id && !selectedUsers.find(s => s.user_id === u.user_id)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error("Search failed:", err);
    }
    setSearchLoading(false);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearchUsers();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUsers([...selectedUsers, user]);
    setSearchResults(searchResults.filter(u => u.user_id !== user.user_id));
    setSearchQuery("");
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.user_id !== userId));
  };

  const handleCreateGroup = async () => {
    if (!name.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    
    if (privacy === "parish_only" && selectedParishIds.length === 0) {
      toast.error("Please select at least one parish");
      return;
    }

    setLoading(true);
    try {
      const payload = { name, description, category, privacy };
      if (privacy === "parish_only") {
        payload.parish_only_parish_ids = selectedParishIds;
      }
      const res = await createGroup(payload);
      setCreatedGroupId(res.data.group_id);
      toast.success("Group created successfully!");
      
      // If there are users to invite, go to step 2
      if (selectedUsers.length > 0) {
        // Send invitations
        try {
          await inviteToGroup(res.data.group_id, selectedUsers.map(u => u.user_id), inviteMessage || null);
          toast.success(`Invitations sent to ${selectedUsers.length} member(s)!`);
        } catch (invErr) {
          toast.error("Group created but failed to send some invitations");
        }
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Create a Group
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Group Details */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Group Name</label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter group name..."
              className="border-[#E5E0D5] font-['Nunito']"
              data-testid="group-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Description</label>
            <Textarea 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this group about?"
              className="border-[#E5E0D5] font-['Nunito'] min-h-[80px]"
              data-testid="group-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']" data-testid="group-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {categories.map(cat => {
                    const Icon = categoryIcons[cat];
                    return (
                      <SelectItem key={cat} value={cat} className="font-['Nunito']">
                        <span className="flex items-center gap-2">
                          {Icon && <Icon className="w-4 h-4" style={{ color: categoryColors[cat] }} />}
                          {cat}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Privacy</label>
              <Select 
                value={privacy} 
                onValueChange={(v) => {
                  setPrivacy(v);
                  if (v === "parish_only" && userParishes.length === 1) {
                    setSelectedParishIds([userParishes[0].parish_id]);
                  }
                }}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']" data-testid="privacy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="public" className="font-['Nunito']">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#556B2F]" />
                      Public
                    </span>
                  </SelectItem>
                  <SelectItem value="private" className="font-['Nunito']">
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      Private
                    </span>
                  </SelectItem>
                  {userParishes.length > 0 && (
                    <SelectItem value="parish_only" className="font-['Nunito']">
                      <span className="flex items-center gap-2">
                        <Church className="w-4 h-4 text-[#4169E1]" />
                        Parish Only
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <p className="text-xs text-[#6B665E] font-['Nunito']">
            {privacy === "public" 
              ? "Anyone can join this group" 
              : privacy === "private"
                ? "Members must be approved by an admin"
                : "Only members of the selected parish(es) can join"}
          </p>
          
          {/* Parish selector for parish_only */}
          {privacy === "parish_only" && userParishes.length > 1 && (
            <div className="space-y-2 p-3 bg-[#F0EBE0]/50 rounded-lg">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Select parishes that can join</label>
              <div className="space-y-1.5">
                {userParishes.map(p => (
                  <label key={p.parish_id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedParishIds.includes(p.parish_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedParishIds([...selectedParishIds, p.parish_id]);
                        } else {
                          setSelectedParishIds(selectedParishIds.filter(id => id !== p.parish_id));
                        }
                      }}
                      className="rounded border-[#E5E0D5]"
                      data-testid={`parish-checkbox-${p.parish_id}`}
                    />
                    <Church className="w-3.5 h-3.5 text-[#4169E1]" />
                    <span className="text-sm text-[#2D2A26] font-['Nunito']">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Invite Members Section */}
          <div className="border-t border-[#E5E0D5] pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="w-4 h-4 text-[var(--liturgical-primary)]" />
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Invite Members (Optional)</label>
            </div>
            
            {/* Search Users */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members to invite..."
                className="pl-9 border-[#E5E0D5] font-['Nunito']"
                data-testid="invite-search-input"
              />
              {searchLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#6B665E]" />
              )}
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 border border-[#E5E0D5] rounded-lg max-h-40 overflow-y-auto">
                {searchResults.map(user => (
                  <button
                    key={user.user_id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full flex items-center gap-3 p-2 hover:bg-[#F9F7F2] transition-colors text-left"
                    data-testid={`invite-user-${user.user_id}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                        {user.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#2D2A26] font-['Nunito'] truncate">{user.name}</p>
                      {user.parish_name && (
                        <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">{user.parish_name}</p>
                      )}
                    </div>
                    <Plus className="w-4 h-4 text-[var(--liturgical-primary)]" />
                  </button>
                ))}
              </div>
            )}
            
            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">
                  {selectedUsers.length} member(s) will be invited
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <Badge
                      key={user.user_id}
                      variant="secondary"
                      className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] pl-1 pr-1"
                    >
                      <Avatar className="h-5 w-5 mr-1">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">
                          {user.name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-['Nunito']">{user.name}</span>
                      <button
                        onClick={() => handleRemoveUser(user.user_id)}
                        className="ml-1 hover:bg-[var(--liturgical-primary)]/20 rounded-full p-0.5"
                        data-testid={`remove-invite-${user.user_id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                
                {/* Optional invite message */}
                <div className="mt-3">
                  <Input
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder="Add a personal message (optional)..."
                    className="border-[#E5E0D5] font-['Nunito'] text-sm"
                    data-testid="invite-message-input"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateGroup}
            disabled={loading}
            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
            data-testid="create-group-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {selectedUsers.length > 0 ? `Create & Invite ${selectedUsers.length}` : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
